import React, { useState, useEffect } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval, isBefore, startOfDay } from 'date-fns';
import { Calendar as CalendarIcon, Clock, Trash, Plus, X } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AvailabilityCalendar({ listing, token }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRangeForm, setShowRangeForm] = useState(false);
  const [showBlockForm, setShowBlockForm] = useState(false);
  
  // Range form state
  const [rangeForm, setRangeForm] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
    startTime: '09:00',
    endTime: '18:00',
    slotDuration: 60,
    isRecurring: false,
    recurringDays: [],
  });

  // Block form state
  const [blockForm, setBlockForm] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    fullDay: true,
    startTime: '09:00',
    endTime: '18:00',
  });

  const listingId = listing?.id;

  useEffect(() => {
    if (listingId) {
      loadAvailability();
      loadBlockedTimes();
    }
  }, [listingId, currentMonth]);

  const loadAvailability = async () => {
    setLoading(true);
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(addMonths(currentMonth, 1)), 'yyyy-MM-dd');
      
      const response = await fetch(
        `${API_URL}/api/availability/${listingId}?start_date=${start}&end_date=${end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setAvailability(data);
      }
    } catch (error) {
      console.error('Failed to load availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBlockedTimes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/availability/blocks/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBlockedTimes(data);
      }
    } catch (error) {
      console.error('Failed to load blocked times:', error);
    }
  };

  const handleSaveRange = async () => {
    try {
      const response = await fetch(`${API_URL}/api/availability/range`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          listing_id: listingId,
          start_date: rangeForm.startDate,
          end_date: rangeForm.endDate,
          start_time: rangeForm.startTime,
          end_time: rangeForm.endTime,
          slot_duration: parseInt(rangeForm.slotDuration),
          is_recurring: rangeForm.isRecurring,
          recurring_days: rangeForm.recurringDays,
        }),
      });
      
      if (response.ok) {
        toast.success('Availability saved successfully');
        setShowRangeForm(false);
        loadAvailability();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save availability');
      }
    } catch (error) {
      toast.error('Failed to save availability');
    }
  };

  const handleBlockTime = async () => {
    try {
      const response = await fetch(`${API_URL}/api/availability/block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          listing_id: listingId,
          date: blockForm.date,
          start_time: blockForm.fullDay ? null : blockForm.startTime,
          end_time: blockForm.fullDay ? null : blockForm.endTime,
        }),
      });
      
      if (response.ok) {
        toast.success('Time blocked successfully');
        setShowBlockForm(false);
        loadAvailability();
        loadBlockedTimes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to block time');
      }
    } catch (error) {
      toast.error('Failed to block time');
    }
  };

  const handleUnblock = async (blockId) => {
    try {
      const response = await fetch(`${API_URL}/api/availability/block/${blockId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Block removed');
        loadAvailability();
        loadBlockedTimes();
      }
    } catch (error) {
      toast.error('Failed to remove block');
    }
  };

  const getDateStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const avail = availability.find(a => a.date === dateStr);
    const blocked = blockedTimes.find(b => b.date === dateStr && !b.start_time);
    
    if (blocked) return 'blocked';
    if (avail && avail.available_hours?.length > 0) return 'available';
    return 'unavailable';
  };

  const toggleRecurringDay = (day) => {
    if (rangeForm.recurringDays.includes(day)) {
      setRangeForm({
        ...rangeForm,
        recurringDays: rangeForm.recurringDays.filter(d => d !== day)
      });
    } else {
      setRangeForm({
        ...rangeForm,
        recurringDays: [...rangeForm.recurringDays, day]
      });
    }
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          onClick={() => setShowRangeForm(true)}
          className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
        >
          <Plus size={20} className="mr-2" />
          Set Availability Range
        </Button>
        <Button
          onClick={() => setShowBlockForm(true)}
          variant="outline"
          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
        >
          <X size={20} className="mr-2" />
          Block Dates
        </Button>
      </div>

      {/* Calendar View */}
      <div className="bg-[#121212] border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <div className="flex gap-2">
            <Button
              onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
              variant="outline"
              size="sm"
              className="border-white/20 text-white"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              variant="outline"
              size="sm"
              className="border-white/20 text-white"
            >
              Next
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center text-zinc-500">
            Loading availability...
          </div>
        ) : (
          <>
            <Calendar
              mode="single"
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              modifiers={{
                available: (date) => getDateStatus(date) === 'available',
                blocked: (date) => getDateStatus(date) === 'blocked',
              }}
              modifiersClassNames={{
                available: 'bg-green-500/20 text-green-400',
                blocked: 'bg-red-500/20 text-red-400 line-through',
              }}
              className="rounded-md border border-white/10"
            />
            
            <div className="flex items-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500/20 rounded"></div>
                <span className="text-zinc-400">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500/20 rounded"></div>
                <span className="text-zinc-400">Blocked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-zinc-800 rounded"></div>
                <span className="text-zinc-400">Not Set</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Blocked Times List */}
      {blockedTimes.length > 0 && (
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Blocked Times</h3>
          <div className="space-y-2">
            {blockedTimes.map((block) => (
              <div key={block.id} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 p-3 rounded">
                <div>
                  <span className="text-white">{block.date}</span>
                  {block.start_time ? (
                    <span className="text-zinc-500 ml-2">
                      {block.start_time} - {block.end_time}
                    </span>
                  ) : (
                    <span className="text-red-400 ml-2">Full Day</span>
                  )}
                </div>
                <button
                  onClick={() => handleUnblock(block.id)}
                  className="text-red-400 hover:text-red-300 p-1"
                >
                  <Trash size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Set Range Modal */}
      {showRangeForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] border border-white/10 p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-serif text-white">Set Availability Range</h3>
              <button onClick={() => setShowRangeForm(false)} className="text-zinc-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Start Date</Label>
                  <Input
                    type="date"
                    value={rangeForm.startDate}
                    onChange={(e) => setRangeForm({ ...rangeForm, startDate: e.target.value })}
                    className="bg-[#0A0A0A] border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">End Date</Label>
                  <Input
                    type="date"
                    value={rangeForm.endDate}
                    onChange={(e) => setRangeForm({ ...rangeForm, endDate: e.target.value })}
                    className="bg-[#0A0A0A] border-white/20 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white mb-2 block">Start Time</Label>
                  <Input
                    type="time"
                    value={rangeForm.startTime}
                    onChange={(e) => setRangeForm({ ...rangeForm, startTime: e.target.value })}
                    className="bg-[#0A0A0A] border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white mb-2 block">End Time</Label>
                  <Input
                    type="time"
                    value={rangeForm.endTime}
                    onChange={(e) => setRangeForm({ ...rangeForm, endTime: e.target.value })}
                    className="bg-[#0A0A0A] border-white/20 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white mb-2 block">Slot Duration</Label>
                <select
                  value={rangeForm.slotDuration}
                  onChange={(e) => setRangeForm({ ...rangeForm, slotDuration: parseInt(e.target.value) })}
                  className="w-full bg-[#0A0A0A] border border-white/20 text-white py-2 px-3 rounded"
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rangeForm.isRecurring}
                    onChange={(e) => setRangeForm({ ...rangeForm, isRecurring: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-white">Recurring weekly schedule</span>
                </label>
              </div>

              {rangeForm.isRecurring && (
                <div>
                  <Label className="text-white mb-2 block">Available Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {dayNames.map((day, index) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleRecurringDay(index)}
                        className={`px-3 py-2 rounded border transition-all ${
                          rangeForm.recurringDays.includes(index)
                            ? 'bg-[#D4AF37] border-[#D4AF37] text-black'
                            : 'border-white/20 text-zinc-400 hover:border-white/40'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSaveRange}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                >
                  Save Availability
                </Button>
                <Button
                  onClick={() => setShowRangeForm(false)}
                  className="bg-transparent border border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block Time Modal */}
      {showBlockForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] border border-white/10 p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-serif text-white">Block Time</h3>
              <button onClick={() => setShowBlockForm(false)} className="text-zinc-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-white mb-2 block">Date</Label>
                <Input
                  type="date"
                  value={blockForm.date}
                  onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
                  className="bg-[#0A0A0A] border-white/20 text-white"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={blockForm.fullDay}
                    onChange={(e) => setBlockForm({ ...blockForm, fullDay: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-white">Block entire day</span>
                </label>
              </div>

              {!blockForm.fullDay && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white mb-2 block">Start Time</Label>
                    <Input
                      type="time"
                      value={blockForm.startTime}
                      onChange={(e) => setBlockForm({ ...blockForm, startTime: e.target.value })}
                      className="bg-[#0A0A0A] border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">End Time</Label>
                    <Input
                      type="time"
                      value={blockForm.endTime}
                      onChange={(e) => setBlockForm({ ...blockForm, endTime: e.target.value })}
                      className="bg-[#0A0A0A] border-white/20 text-white"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleBlockTime}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  Block Time
                </Button>
                <Button
                  onClick={() => setShowBlockForm(false)}
                  className="bg-transparent border border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
